import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { RootState } from '../app/store';
import { replaceWithComboItem } from '../reducers/menuSlice';
import { cartActions } from '../reducers/cartSlice';
import { analyzeItemConversion } from '../utils/itemConversion';
import { analyzeOptionReplacement } from '../utils/optionReplacement';
import { ParsedModifierGroup } from '../types/menu';
import { CartItem } from '../utils/cart';
import { getMenuUnificationFeatureFlag } from '../utils/session-storage';
import { selectFeatureFlagItemConversion } from '../redux/features/config/config.selector';
import {
  selectItemConversionBusinessRules,
  selectUnavailableItemsByName,
} from '../selectors/menu';
import { IBusinessRule } from '../types/brandMenu';

/**
 * Hook for handling both item conversion and option replacement
 */
const useItemConversion = () => {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const isItemConversionEnabled = useSelector(selectFeatureFlagItemConversion);
  const {
    comboConversion,
    aLaCarteConversion,
    defaultSizeSelection,
    comboSizeTransformation,
  } = useSelector(selectItemConversionBusinessRules);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);

  // Extract combo conversion business rules from brand menu
  const itemConversionRules: IBusinessRule[] = useMemo(
    () => [...(comboConversion || []), ...(aLaCarteConversion || [])],
    [comboConversion, aLaCarteConversion]
  );

  const handleItemConversion = useCallback(
    (
      modGroup: ParsedModifierGroup,
      selectedMenuItemName: string,
      currentCartItem: CartItem
    ) => {
      const conversionInfo = analyzeItemConversion(
        modGroup.name || '',
        selectedMenuItemName,
        currentCartItem,
        itemConversionRules
      );

      if (conversionInfo.shouldConvert) {
        dispatch(
          replaceWithComboItem({
            originalCartItem: currentCartItem,
            targetItemName: conversionInfo.targetItemId!,
            comboSize: conversionInfo.conversionSize,
            optionsToPreSelect: conversionInfo.optionsToPreSelect,
          })
        );
      }
    },
    [dispatch, itemConversionRules]
  );

  // Extract option replacement business rules (default size selection + combo size transformation)
  const optionReplacementRules: IBusinessRule[] = useMemo(
    () => [...(defaultSizeSelection || []), ...(comboSizeTransformation || [])],
    [defaultSizeSelection, comboSizeTransformation]
  );

  const handleOptionReplacement = useCallback(
    (
      modGroup: ParsedModifierGroup,
      selectedMenuItemName: string,
      currentCartItem: CartItem
    ) => {
      // The caller dispatches the triggering modifier selection (e.g. the new combo
      // size) synchronously before invoking this, but `currentCartItem` is the
      // pre-selection snapshot captured during the component's render. Re-read the
      // freshly-updated cart item from the store so availability checks evaluate
      // against the new selection instead of the previous one — otherwise a combo
      // size change is checked against the prior size and the dependent side/drink
      // sizes are wrongly judged unavailable.
      const latestCartItem =
        (currentCartItem &&
          store.getState().cart?.cartItems?.[currentCartItem.cartItemId]) ??
        currentCartItem;

      const replacementInfo = analyzeOptionReplacement(
        modGroup.name || '',
        selectedMenuItemName,
        latestCartItem,
        optionReplacementRules,
        unavailableItemsByName
      );

      if (replacementInfo.shouldApplyReplacement) {
        // Apply option replacement by dispatching the new action
        dispatch(
          cartActions.applyOptionReplacement({
            cartItemId: latestCartItem.cartItemId,
            targetModifierGroups: replacementInfo.targetModifierGroups,
            unavailableItemsByName,
          })
        );
      }
    },
    [dispatch, store, optionReplacementRules, unavailableItemsByName]
  );

  const handleModifierSelection = useCallback(
    (
      modGroup: ParsedModifierGroup,
      selectedMenuItemName: string,
      currentCartItem: CartItem
    ) => {
      // Check feature flags before running item conversion or option replacement
      // Both unified menu and item conversion feature flags must be enabled
      const isUnifiedMenu = getMenuUnificationFeatureFlag();
      if (!isUnifiedMenu || !isItemConversionEnabled) {
        return;
      }

      // First check for item conversion
      const conversionInfo = analyzeItemConversion(
        modGroup.name || '',
        selectedMenuItemName,
        currentCartItem,
        itemConversionRules
      );

      if (conversionInfo.shouldConvert) {
        // If conversion happens, let the thunk handle option replacement
        dispatch(
          replaceWithComboItem({
            originalCartItem: currentCartItem,
            targetItemName: conversionInfo.targetItemId!,
            comboSize: conversionInfo.conversionSize,
            optionsToPreSelect: conversionInfo.optionsToPreSelect,
          })
        );
      } else {
        // Only check for option replacement if no conversion happens
        handleOptionReplacement(
          modGroup,
          selectedMenuItemName,
          currentCartItem
        );
      }
    },
    [
      dispatch,
      handleOptionReplacement,
      isItemConversionEnabled,
      itemConversionRules,
    ]
  );

  return {
    handleItemConversion,
    handleOptionReplacement,
    handleModifierSelection,
  };
};

export default useItemConversion;
